import CallMadeOutlinedIcon from '@mui/icons-material/CallMadeOutlined'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import InfoIcon from '@mui/icons-material/Info'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { Box, Button, IconButton, List, ListItem, Modal, Tooltip, Typography } from '@mui/material'
import { styled } from '@mui/system'
import { useQuery } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { dashboardsService } from '../../api/services/dashboards.service'
import { liveDashboardsService } from '../../api/services/liveDashboards.service'
import { useAppSelector, useCurrentSite } from '../../store/store.hooks'
import { Dashboard, LiveDashboard } from '../../types'
import LiveDashboardModalContent from './live/LiveDashboardModalContent'
import LiveDashboardPreview from './live/LiveDashboardPreview'
import { LIVE_URL } from './live/useLiveEmbed'

// Product pages whose row shows the live cards. Empty for now: cloud dashboards are reached from the sidebar's
// Dashboards section (portal/src/dashboards/). Add a product name (e.g. 'WeSaw') to show them in its row again.
const LIVE_CARDS_PRODUCTS: readonly string[] = []

const HorizontalList = styled(List)(() => ({
  display: 'flex',
  flexDirection: 'row',
  overflowX: 'hidden',
  listStyle: 'none',
  padding: 0,
  scrollBehavior: 'smooth',
}))

const DashboardItem = styled(Box)(() => ({
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  width: '192px',
  height: 'auto',
  borderRadius: '6px',
  '&:hover': {
    backgroundColor: '#c8c9cb',
  },
  padding: '10px',
  paddingBottom: '10px',
  paddingTop: '10px',
}))

const base64ToImage = (base64: string) => `data:image/jpeg;base64,${base64}`
const replaceServerIP = (url: string, newIP: string) => url.replace(/http:\/\/[^:]+/, `http://${newIP}`)

// Mill dashboards show their screenshot; cloud dashboards show the live app (see live/).
type DashboardRow =
  | { kind: 'mill'; id: string; title: string; dashboard: Dashboard }
  | { kind: 'live'; id: string; title: string; dashboard: LiveDashboard }

const Dashboards = ({ isConnected, productName }: { isConnected: boolean; productName: string }) => {
  const serverIP = useAppSelector((state) => state.auth.workingAddress)
  const [selectedImage, setSelectedImage] = useState<string | undefined>(undefined)
  const [currentDashboardUrl, setCurrentDashboardUrl] = useState<string | undefined>(undefined)
  const [selectedLive, setSelectedLive] = useState<LiveDashboard | undefined>(undefined)

  const currentSite = useCurrentSite()

  const { data: dashboardsData = [] } = useQuery<Dashboard[]>({
    queryKey: ['dashboards', productName, serverIP, currentSite?.id],
    queryFn: () => {
      if (!currentSite) return Promise.resolve([])
      return dashboardsService.listByLicense(currentSite.id, productName)
    },
    refetchInterval: 5 * 60 * 1000,
    select: (data) =>
      data
        .sort((a, b) => a.title.localeCompare(b.title))
        .map((dashboard) => ({
          ...dashboard,
          urlToDashboard: serverIP ? replaceServerIP(dashboard.urlToDashboard, serverIP) : dashboard.urlToDashboard,
        })),
  })

  // Independent of the screenshots: a failure here only hides the live cards.
  const { data: liveDashboardsData = [] } = useQuery<LiveDashboard[]>({
    queryKey: ['liveDashboards', productName, currentSite?.id],
    queryFn: () => liveDashboardsService.listByLicense(currentSite!.id, productName),
    enabled: !!currentSite && !!LIVE_URL && LIVE_CARDS_PRODUCTS.includes(productName),
  })

  const rows: DashboardRow[] = [
    ...dashboardsData.map(
      (dashboard): DashboardRow => ({
        kind: 'mill',
        id: dashboard.id,
        title: dashboard.title,
        dashboard,
      }),
    ),
    ...liveDashboardsData.map(
      (dashboard): DashboardRow => ({
        kind: 'live',
        id: dashboard.Id,
        title: dashboard.Name,
        dashboard,
      }),
    ),
  ].sort((a, b) => a.title.localeCompare(b.title))

  const { t } = useTranslation()

  const handleImageClick = (base64: string, url: string) => {
    setSelectedImage(base64ToImage(base64))
    setCurrentDashboardUrl(url)
  }

  const handleClose = () => {
    setSelectedImage(undefined)
    setCurrentDashboardUrl(undefined)
    setSelectedLive(undefined)
  }
  const HEIGHT = 48
  const scrollRef = useRef<HTMLUListElement>(null)
  const SCROLL_AMOUNT = 400

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: direction === 'left' ? -SCROLL_AMOUNT : SCROLL_AMOUNT, behavior: 'smooth' })
    }
  }

  return (
    <Box sx={{ display: 'inline', flexDirection: 'rows' }}>
      <Box
        sx={{
          height: HEIGHT,
          display: 'flex',
          alignItems: 'center',
          padding: 1,
          backgroundColor: '#0072CE',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Typography
            variant="body2"
            sx={{ fontSize: '17px', fontWeight: 600, color: '#ffffff', textTransform: 'uppercase' }}
          >
            {t('dashboards.dashboards')}
          </Typography>
          <Tooltip title={t('dashboards.refreshEvery5Minutes')} placement="right" sx={{ color: '#ffffff' }}>
            <IconButton size="small">
              <InfoIcon />
            </IconButton>
          </Tooltip>
        </div>
        <Tooltip title={isConnected ? t('connection.documentationToolNotAvailable') : ''} followCursor>
          <span>
            <Button
              id="documentation-button"
              component="a"
              href={`http://${serverIP}:49989`}
              target="_blank"
              rel="noopener noreferrer"
              disabled={!isConnected}
              sx={{
                cursor: isConnected ? 'pointer' : 'not-allowed',
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: isConnected ? '#ffffff' : 'grey', fontWeight: 'bold', marginLeft: '4px' }}
              >
                {t('tools.downtimeDocumentation')}
              </Typography>
              <CallMadeOutlinedIcon sx={{ fontSize: '0.9rem', color: isConnected ? '#ffffff' : 'grey' }} />
            </Button>
          </span>
        </Tooltip>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <IconButton
          onClick={() => scroll('left')}
          sx={{ mr: '25px', ml: '25px', border: '2px solid', borderRadius: '50%', fontSize: '1.5rem' }}
        >
          <ChevronLeftIcon fontSize="inherit" />
        </IconButton>
        <HorizontalList ref={scrollRef} sx={{ overflow: 'hidden', flex: 1 }}>
          {rows.map((row) => (
            // Top-aligned: a live card is taller (status badge under its title) but its preview lines up.
            <ListItem
              key={`${row.kind}-${row.id}`}
              sx={{ justifyContent: 'center', alignItems: 'flex-start', display: 'flex' }}
            >
              <Tooltip title={row.title} placement="top">
                <DashboardItem
                  sx={{
                    color: 'text.primary',
                    pointerEvents: 'auto',
                  }}
                >
                  {row.kind === 'mill' ? (
                    <>
                      <img
                        src={base64ToImage(row.dashboard.thumbnail)}
                        alt={row.title}
                        style={{
                          width: '100%',
                          cursor: 'zoom-in',
                          borderRadius: '6px',
                        }}
                        onClick={() => handleImageClick(row.dashboard.thumbnail, row.dashboard.urlToDashboard)}
                      />
                      <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {row.title}
                        </Typography>
                      </Box>
                    </>
                  ) : (
                    <LiveDashboardPreview
                      siteId={row.dashboard.SiteId}
                      dashboardId={row.dashboard.Id}
                      title={row.title}
                      onOpen={() => setSelectedLive(row.dashboard)}
                    />
                  )}
                </DashboardItem>
              </Tooltip>
            </ListItem>
          ))}
        </HorizontalList>
        <IconButton
          onClick={() => scroll('right')}
          sx={{ mr: '25px', ml: '25px', border: '2px solid', borderRadius: '50%', fontSize: '1.5rem' }}
        >
          <ChevronRightIcon id="dashboards-navigation" fontSize="inherit" />
        </IconButton>
        {selectedImage ? (
          <Modal
            open={!!selectedImage}
            onClose={handleClose}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <Box
              sx={{
                maxWidth: '90%',
                maxHeight: '90%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
              }}
            >
              <img
                src={selectedImage}
                alt="Full size"
                style={{
                  width: '90%',
                  maxWidth: '90%',
                  maxHeight: '90%',
                  objectFit: 'contain',
                  cursor: 'pointer',
                }}
                onClick={handleClose}
              />
              <Tooltip title={!isConnected ? t('connection.dashboardAndToolsNotAvailable') : ''} followCursor>
                <span>
                  <Button
                    startIcon={<OpenInNewIcon />}
                    {...(isConnected && currentDashboardUrl
                      ? { href: currentDashboardUrl, target: '_blank', rel: 'noopener noreferrer' }
                      : {})}
                    variant="contained"
                    color="primary"
                    sx={{
                      fontSize: '0.7rem',
                      position: 'absolute',
                      bottom: 16,
                      left: 16,
                    }}
                    disabled={!isConnected}
                  >
                    {t('dashboards.openLiveDashboard')}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Modal>
        ) : null}
        {selectedLive ? (
          <Modal open onClose={handleClose} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Box>
              <LiveDashboardModalContent
                siteId={selectedLive.SiteId}
                dashboardId={selectedLive.Id}
                title={selectedLive.Name}
              />
            </Box>
          </Modal>
        ) : null}
      </Box>
    </Box>
  )
}

export default Dashboards
